import React, { useState } from 'react';
import { useDashboard } from './DashboardContext';
import { BookOpen, X, ChevronDown, ChevronUp, FileText } from 'lucide-react';

const JournalPage = () => {
  const {
    trades,
    showTradeModal,
    setShowTradeModal,
    editingTrade,
    setEditingTrade,
    tradeForm,
    setTradeForm,
    newPairName,
    setNewPairName,
    defaultPairs,
    customPairs,
    handleTradeFormChange,
    handleAddTrade,
    handleEditTrade,
    handleDeleteTrade,
    handleAddCustomPair
  } = useDashboard();

  // Track which trade's notes are expanded
  const [expandedNotes, setExpandedNotes] = useState({});

  const toggleNotes = (tradeId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [tradeId]: !prev[tradeId]
    }));
  };

  // Time formatting helper - converts "943" to "9:43"
  const formatTimeInput = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 0) return '';
    if (digits.length <= 2) return digits;
    if (digits.length === 3) return `${digits[0]}:${digits.slice(1)}`;
    if (digits.length >= 4) return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
    return value;
  };

  const handleTimeChange = (e) => {
    const formatted = formatTimeInput(e.target.value);
    const currentTime = tradeForm.time || '';
    const currentPeriod = currentTime.includes('PM') ? 'PM' : 'AM';
    const newTime = formatted ? `${formatted} ${currentPeriod}` : '';
    setTradeForm(prev => ({ ...prev, time: newTime }));
  };

  const handlePeriodChange = (period) => {
    const currentTime = tradeForm.time || '';
    const timeWithoutPeriod = currentTime.replace(/\s*(AM|PM)\s*/i, '').trim();
    const newTime = timeWithoutPeriod ? `${timeWithoutPeriod} ${period}` : '';
    setTradeForm(prev => ({ ...prev, time: newTime }));
  };

  const getTimeParts = () => {
    const time = tradeForm.time || '';
    const timeWithoutPeriod = time.replace(/\s*(AM|PM)\s*/i, '').trim();
    const period = time.includes('PM') ? 'PM' : 'AM';
    return { timeValue: timeWithoutPeriod, period };
  };

  return (
    <div className="page-content">
      <div className="journal-header-section">
        <div>
          <h1 className="page-title">Trading Journal</h1>
          <p className="page-description">Track and analyze your trades</p>
        </div>
        <button className="add-trade-button" onClick={() => setShowTradeModal(true)}>
          Add Trade
        </button>
      </div>

      <div className="journal-content-card">
        <h3 className="card-section-title">Trade History</h3>
        {trades.length === 0 ? (
          <div className="empty-state-large">
            <BookOpen size={48} className="empty-icon" />
            <p className="empty-message">No trades logged yet. Start tracking your trades!</p>
          </div>
        ) : (
          <div className="trades-table">
            <table className="journal-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Pair</th>
                  <th>Direction</th>
                  <th>Session</th>
                  <th>Entry</th>
                  <th>Exit</th>
                  <th>P&L</th>
                  <th>Outcome</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...trades]
                  .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort by date, newest first
                  .map((trade) => {
                  // Format date without timezone conversion
                  const formatTradeDate = (dateStr) => {
                    if (!dateStr) return 'N/A';
                    // If already in YYYY-MM-DD format, parse it directly
                    if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
                      const [year, month, day] = dateStr.split('T')[0].split('-');
                      return new Date(year, month - 1, day).toLocaleDateString();
                    }
                    return new Date(dateStr).toLocaleDateString();
                  };
                  return (
                  <React.Fragment key={trade.id}>
                  <tr className={expandedNotes[trade.id] ? 'notes-expanded-parent' : ''}>
                    <td>{formatTradeDate(trade.date)}</td>
                    <td className="pair-cell">{trade.pair}</td>
                    <td>
                      <span className={`direction-badge ${trade.direction.toLowerCase()}`}>
                        {trade.direction}
                      </span>
                    </td>
                    <td>{trade.session}</td>
                    <td>{trade.entry}</td>
                    <td>{trade.outcome === 'Win' ? trade.takeProfit : trade.stopLoss}</td>
                    <td className={`pl-cell ${trade.outcome === 'Win' ? 'positive' : 'negative'}`}>
                      {trade.outcome === 'Loss' ? '-' : ''}${trade.profitLoss}
                    </td>
                    <td>
                      <span className={`outcome-badge ${trade.outcome.toLowerCase()}`}>
                        {trade.outcome}
                      </span>
                    </td>
                    <td>
                      <div className="trade-actions">
                        {trade.notes && trade.notes.trim() && (
                          <button 
                            className={`notes-toggle-btn ${expandedNotes[trade.id] ? 'active' : ''}`}
                            onClick={() => toggleNotes(trade.id)}
                            data-testid={`notes-toggle-${trade.id}`}
                            title={expandedNotes[trade.id] ? 'Hide notes' : 'View notes'}
                          >
                            <FileText size={16} />
                            {expandedNotes[trade.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </button>
                        )}
                        <button 
                          className="edit-trade-btn"
                          onClick={() => handleEditTrade(trade)}
                        >
                          Edit
                        </button>
                        <button 
                          className="delete-trade-btn"
                          onClick={() => handleDeleteTrade(trade.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  {trade.notes && trade.notes.trim() && expandedNotes[trade.id] && (
                    <tr className="notes-expanded-row" data-testid={`notes-row-${trade.id}`}>
                      <td colSpan="9">
                        <div className="trade-notes-content">
                          <FileText size={16} className="notes-icon" />
                          <div className="notes-text">{trade.notes}</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showTradeModal && (
        <div className="modal-overlay" onClick={() => { setShowTradeModal(false); setEditingTrade(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingTrade ? 'Edit Trade' : 'Add New Trade'}</h2>
              <button className="modal-close" onClick={() => { setShowTradeModal(false); setEditingTrade(null); }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-form-grid">
                <div className="form-group-modal">
                  <label>Trade Date</label>
                  <input 
                    type="date" 
                    name="date" 
                    value={tradeForm.date} 
                    onChange={handleTradeFormChange}
                    className="date-picker-input"
                  />
                </div>
                <div className="form-group-modal">
                  <label>Pair</label>
                  <select name="pair" value={tradeForm.pair} onChange={handleTradeFormChange}>
                    <option value="">Select a pair</option>
                    {defaultPairs.map(pair => (
                      <option key={pair} value={pair}>{pair}</option>
                    ))}
                    {customPairs.map(pair => (
                      <option key={pair} value={pair}>{pair}</option>
                    ))}
                    <option value="__add_new__">+ Add New Pair</option>
                  </select>
                  {tradeForm.pair === '__add_new__' && (
                    <div className="add-pair-inline">
                      <input 
                        type="text" 
                        placeholder="Enter pair name (e.g., BTCUSD)"
                        value={newPairName}
                        onChange={(e) => setNewPairName(e.target.value.toUpperCase())}
                        onKeyPress={async (e) => {
                          if (e.key === 'Enter' && newPairName.trim()) {
                            const pairToAdd = newPairName.trim().toUpperCase();
                            await handleAddCustomPair(pairToAdd);
                            setTradeForm({...tradeForm, pair: pairToAdd});
                          }
                        }}
                      />
                      <button 
                        className="add-pair-btn"
                        onClick={async () => {
                          if (newPairName.trim()) {
                            const pairToAdd = newPairName.trim().toUpperCase();
                            await handleAddCustomPair(pairToAdd);
                            setTradeForm({...tradeForm, pair: pairToAdd});
                          }
                        }}
                      >
                        Add
                      </button>
                    </div>
                  )}
                </div>
                <div className="form-group-modal">
                  <label>Direction</label>
                  <select name="direction" value={tradeForm.direction} onChange={handleTradeFormChange}>
                    <option value="Long">Long</option>
                    <option value="Short">Short</option>
                  </select>
                </div>
                <div className="form-group-modal">
                  <label>Session</label>
                  <select name="session" value={tradeForm.session} onChange={handleTradeFormChange}>
                    <option value="New York">New York</option>
                    <option value="London">London</option>
                    <option value="Tokyo">Tokyo</option>
                    <option value="Sydney">Sydney</option>
                  </select>
                </div>
                <div className="form-group-modal">
                  <label>Time</label>
                  <div className="time-input-group">
                    <input 
                      type="text" 
                      name="time" 
                      value={getTimeParts().timeValue} 
                      onChange={handleTimeChange} 
                      placeholder="9:43"
                      maxLength={5}
                      className="time-input"
                    />
                    <div className="time-period-toggle">
                      <button 
                        type="button"
                        className={`period-btn ${getTimeParts().period === 'AM' ? 'active' : ''}`}
                        onClick={() => handlePeriodChange('AM')}
                      >
                        AM
                      </button>
                      <button 
                        type="button"
                        className={`period-btn ${getTimeParts().period === 'PM' ? 'active' : ''}`}
                        onClick={() => handlePeriodChange('PM')}
                      >
                        PM
                      </button>
                    </div>
                  </div>
                </div>
                <div className="form-group-modal">
                  <label>Outcome</label>
                  <select name="outcome" value={tradeForm.outcome} onChange={handleTradeFormChange}>
                    <option value="Win">Win</option>
                    <option value="Loss">Loss</option>
                    <option value="Breakeven">Breakeven</option>
                  </select>
                </div>
                <div className="form-group-modal">
                  <label>Entry</label>
                  <input type="number" name="entry" value={tradeForm.entry} onChange={handleTradeFormChange} step="0.00001" />
                </div>
                <div className="form-group-modal">
                  <label>Stop Loss</label>
                  <input type="number" name="stopLoss" value={tradeForm.stopLoss} onChange={handleTradeFormChange} step="0.00001" />
                </div>
                <div className="form-group-modal">
                  <label>Take Profit</label>
                  <input type="number" name="takeProfit" value={tradeForm.takeProfit} onChange={handleTradeFormChange} step="0.00001" />
                </div>
                <div className="form-group-modal">
                  <label>Lot/Contract Size</label>
                  <input type="number" name="lotSize" value={tradeForm.lotSize} onChange={handleTradeFormChange} step="0.01" />
                </div>
                <div className="form-group-modal">
                  <label>Pips/Points</label>
                  <input type="number" name="pipsPoints" value={tradeForm.pipsPoints} onChange={handleTradeFormChange} />
                </div>
                <div className="form-group-modal full-width">
                  <label>Profit/Loss ($)</label>
                  <input type="number" name="profitLoss" value={tradeForm.profitLoss} onChange={handleTradeFormChange} step="0.01" />
                </div>
                <div className="form-group-modal full-width">
                  <label>Notes</label>
                  <textarea name="notes" value={tradeForm.notes} onChange={handleTradeFormChange} rows="4" placeholder="Trade notes..."></textarea>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-button" onClick={() => { setShowTradeModal(false); setEditingTrade(null); }}>Cancel</button>
              <button className="add-trade-modal-button" onClick={handleAddTrade}>
                {editingTrade ? 'Save Changes' : 'Add Trade'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalPage;